'use client';

import { motion } from 'framer-motion';
import type { ReactNode } from 'react';

const CARD_ANIMATION = {
  hidden: { opacity: 0, scale: 0.95, y: 20 },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.25, 0.1, 0.25, 1],
    },
  },
};

const ICON_ANIMATION = {
  initial: { scale: 0 },
  animate: { scale: 1 },
  transition: (delay: number) => ({
    delay,
    duration: 0.5,
    ease: [0.25, 0.1, 0.25, 1],
  }),
};

type BenefitCardAnimatedProps = {
  children: ReactNode;
  iconDelay?: number;
  className?: string;
};

export const BenefitCardAnimated = ({
  children,
  iconDelay = 0.3,
  className = 'bg-Gray-white flex-1 h-220 flex flex-col gap-10 items-center justify-center px-16 rounded-xlarge',
}: BenefitCardAnimatedProps) => {
  return (
    <motion.article
      variants={CARD_ANIMATION}
      className={className}
      whileHover={{ scale: 1.02, transition: { duration: 0.2 } }}
    >
      <motion.div
        {...ICON_ANIMATION}
        transition={ICON_ANIMATION.transition(iconDelay)}
      >
        {children}
      </motion.div>
    </motion.article>
  );
};

type SpecialBenefitCardAnimatedProps = {
  children: ReactNode;
};

export const SpecialBenefitCardAnimated = ({
  children,
}: SpecialBenefitCardAnimatedProps) => {
  return (
    <motion.article
      variants={CARD_ANIMATION}
      className="relative flex h-156 flex-1 flex-col items-center justify-center rounded-xlarge bg-Gray-25 px-16 py-46"
    >
      {children}
    </motion.article>
  );
};

type PlusIconProps = {
  delay: number;
  isInView: boolean;
};

export const PlusIcon = ({ delay, isInView }: PlusIconProps) => {
  return (
    <motion.div
      initial={{ scale: 0, rotate: -180, opacity: 0 }}
      animate={
        isInView
          ? { scale: 1, rotate: 0, opacity: 1 }
          : { scale: 0, rotate: -180, opacity: 0 }
      }
      transition={{
        delay,
        duration: 0.5,
        ease: [0.25, 0.1, 0.25, 1],
      }}
      className="absolute -right-14 top-68 z-[1] -translate-y-1/2 translate-x-1/2"
    >
      <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <circle cx="12" cy="12" r="12" fill="url(#paint0_linear_plus)" />
        <path
          d="M17.91 10.0C18.02 11.18 18.04 12.87 17.91 13.99C17.25 14.07 15.79 14.1 14.1 14.1C14.1 15.79 14.07 17.25 13.99 17.91C12.87 18.04 11.18 18.02 10.0 17.91C9.95 17.04 9.92 15.63 9.92 14.07C8.28 14.04 6.82 14.019 6.08 13.99C5.97 13.12 5.97 10.87 6.08 10.0C6.82 9.97 8.25 9.92 9.92 9.92C9.92 8.25 9.97 6.82 10.0 6.08C10.87 5.97 13.12 5.97 13.99 6.08C14.019 6.82 14.04 8.28 14.07 9.92C15.63 9.92 17.04 9.95 17.91 10.0Z"
          fill="white"
        />
        <defs>
          <linearGradient
            id="paint0_linear_plus"
            x1="0"
            y1="12"
            x2="24"
            y2="12"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#FF8428" />
            <stop offset="1" stopColor="#FF4800" />
          </linearGradient>
        </defs>
      </svg>
    </motion.div>
  );
};
